import axios from 'axios';

const API_KEY = process.env.REACT_APP_API_KEY;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export const getChatbotSettings = async () => {
  try {
    const response = await axios.get(`${API_URL}/get_chatbot_settings`, {
      withCredentials: true
    });
    return response.data; 
  } catch (error) {
    throw error.response?.data || { message: 'Failed to load chatbot settings' };
  }
};

export const loginUser = async (email, password) => {
  try {
    const response = await axios.post(`${API_URL}/login`, 
      { email, password },
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Login failed' };
  }
};

export const signupUser = async (name, email, password) => {
  try {
    const response = await axios.post(`${API_URL}/signup`,
      { name, email, password },
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Signup failed' };
  }
};

export const checkAuthStatus = async () => {
  try {
    const response = await axios.get(`${API_URL}/auto_login`, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { status: 'error', message: 'Failed to check auth status' };
  }
};

// Password Reset APIs
export const requestPasswordReset = async (email) => {
  try {
    const response = await axios.post(`${API_URL}/forgot-password`, { email });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to request password reset' };
  }
};

export const verifyResetOtp = async (email, otp) => {
  try {
    const response = await axios.post(`${API_URL}/verify-reset-otp`, { email, otp });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to verify OTP' };
  }
};

export const resetUserPassword = async (email, otp, newPassword) => {
  try {
    const response = await axios.post(`${API_URL}/reset-password`, { 
      email, 
      otp, 
      new_password: newPassword 
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to reset password' };
  }
};

export const uploadFiles = async (files) => {
  const formData = new FormData();
  for (let file of files) {
    // console.log('Adding file to FormData:', file.name, file.type);
    formData.append('files', file);
  }
  
  try {
    // console.log('Sending request to:', `${API_URL}/upload`);
    const response = await axios.post(`${API_URL}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        // console.log('Upload progress:', percentCompleted);
      },
    });
    // console.log('Upload response:', response.data);
    return response.data.texts;
  } catch (error) {
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      headers: error.response?.headers
    });
    throw new Error(error.response?.data?.error || 'Failed to upload files');
  }
};

export const saveChatbotSettings = async ({ model, response_style, history_length, chatbot_type }) => {
  try {
    const response = await axios.post(`${API_URL}/save_chatbot_settings`, {
      model,
      response_style,
      history_length,
      chatbot_type
    }, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error saving chatbot settings:', error);
    throw new Error(error.response?.data?.error || error.message || 'Failed to save chatbot settings');
  }
};

export const trainBot = async (documents) => {
  try {
    const response = await axios.post(`${API_URL}/train_chatbot`, 
      { text_data: documents },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error training bot:', error);
    throw new Error(error.response?.data?.error || 'Failed to train bot');
  }
};

export async function sendChatMessage({ chatbot_id, messages, conversation_id, jwtToken, is_flow_end, question_index, chatbot_type }) {
  const response = await fetch(`${API_URL}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwtToken}`
    },
    body: JSON.stringify({ 
      chatbot_id,
      messages,
      conversation_id,
      is_flow_end,
      question_index,
      chatbot_type
    }),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response;
}

export async function getCosts(userId, startDate, endDate) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/costs?user_id=${userId}&start_date=${startDate}&end_date=${endDate}`,
      {
        headers: {
          'Authorization': `Bearer ${API_KEY}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error getting costs:', error);
    throw new Error(error.response?.data?.error || 'Failed to get costs');
  }
}

export const getDashboardOverview = async () => {
  try {
    const res = await axios.get(`${API_URL}/dashboard/overview`, {
      withCredentials: true
    });
    
    if (res.data.status === 'success') {
      return {
        status: res.data.status,
        data: {
          kpiStats: res.data.data.kpiStats,
          monthlyConversations: res.data.data.monthlyConversations,
          costUsageOverview: res.data.data.costUsageOverview
        }
      };
    }
    
    return res.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to get dashboard overview' };
  }
};

export const createStripeCheckoutSession = async ({ package_key, frontend_url, custom_plan_amount = null }) => {
  try {
    const response = await axios.post(
      `${API_URL}/payments/create-checkout-session`, 
      { 
        package_key,
        frontend_url,
        ...(custom_plan_amount !== null && { custom_plan_amount })
      },
      {
        withCredentials: true
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating Stripe checkout session:', error.response?.data || error.message);
    throw error.response?.data || { message: 'Failed to create Stripe checkout session' };
  }
};

export const createPayPalOrder = async ({ package_key, frontend_url, custom_plan_amount = null }) => {
  try {
    const response = await axios.post(
      `${API_URL}/payments/create-paypal-order`, 
      { 
        package_key,
        frontend_url,
        ...(custom_plan_amount !== null && { custom_plan_amount })
      },
      {
        withCredentials: true
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating PayPal order:', error.response?.data || error.message);
    throw error.response?.data || { message: 'Failed to create PayPal order' };
  }
};

export const verifyStripeSessionStatus = async (sessionId) => {
  try {
    const response = await axios.post(`${API_URL}/payments/verify-session-status`, 
      { session_id: sessionId },
      {
        withCredentials: true
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error verifying Stripe session:', error.response?.data || error.message);
    throw error.response?.data || { message: 'Failed to verify Stripe session' };
  }
};

export const getUserPaymentHistory = async (limit = 10) => {
  try {
    const response = await axios.get(`${API_URL}/payments/history`, {
      params: { limit },
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching payment history:', error.response?.data || error.message);
    throw error.response?.data || { message: 'Failed to fetch payment history' };
  }
};

export const testChatbot = async (messages, question_index, is_flow_end, chatbot_type ) => {
  try {
    const response = await fetch(`${API_URL}/test_chatbot`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ 
        messages,
        question_index,
        is_flow_end,
        chatbot_type
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response;
  } catch (error) {
    console.error('Error testing chatbot:', error);
    throw error;
  }
};

export async function startConversation() {
  const chatbot_id = localStorage.getItem('chatbot_id');
  const jwtToken = localStorage.getItem('jwtToken');
  const response = await axios.post(
    `${API_URL}/conversations/start`,
    { chatbot_id },
    {
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        'Content-Type': 'application/json'
      }
    }
  );
  return response.data;  
}

export const uploadIcon = async (iconFile) => {
  try {
    const formData = new FormData();
    formData.append('icon', iconFile);

    const response = await axios.post(`${API_URL}/upload_icon`, formData, {
      withCredentials: true,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to upload icon' };
  }
};

// In api.js
export const getAvailableModels = async () => {
  try {
    const response = await axios.get(`${API_URL}/models_info`, {
      withCredentials: true
    });
    return response.data.models;
  } catch (error) {
    console.error('Error fetching models:', error);
    throw error.response?.data || { message: 'Failed to load available models' };
  }
};

export const sendGoogleIdToken = async (idToken) => {
  try {
    const response = await axios.post(
      `${API_URL}/auth/google`,
      { id_token: idToken },
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to authenticate with Google' };
  }
};

// Start a new conversation using /first_chat
export async function startFirstChat({ chatbot_id, messages, chatbot_type }) {
  const response = await fetch(`${API_URL}/first_chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ chatbot_id, messages, chatbot_type }),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response;
}

// get training data
export const getTrainingData = async () => {
  try {
    const response = await axios.get(`${API_URL}/get_training_data`, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching training data:', error);
    throw error.response?.data || error;
  }
};

// delete training data
export const deleteTrainingData = async () => {
  try {
    const response = await axios.delete(`${API_URL}/delete_training_data`, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting training data:', error);
    throw error.response?.data || error;
  }
};

export const getUserPaymentDetails = async () => {
  try {
    const response = await axios.get(`${API_URL}/payments/user-payment-details`, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user payment details:', error.response?.data || error.message);
    throw error.response?.data || { message: 'Failed to fetch user payment details' };
  }
};

export const logoutUser = async () => {
  try {
    const response = await axios.post(`${API_URL}/logout`, {}, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.error('Error logging out:', error.response?.data || error.message);
    throw error.response?.data || { message: 'Failed to logout' };
  }
};